/**
 * Actor Descriptor Hook
 *
 * FERP CRITICAL: Advanced actor descriptor management
 *
 * FERP Principles:
 * - Repository pattern (GraphQL-only data access)
 * - Backend ID generation (Snowflake IDs only)
 * - Data-driven (all actor definitions from backend)
 * - Life-critical (complete error handling)
 * - Domain-agnostic (works for all actor types)
 *
 * Features:
 * - Actor descriptor CRUD operations
 * - Actor capability management
 * - Actor permission tracking
 * - Actor versioning
 * - Actor configuration management
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';

// ============================================================================
// Types
// ============================================================================

export type ActorType = 'system' | 'custom' | 'external' | 'composite';

export interface RetryPolicy {
  max_attempts: number;
  backoff_seconds: number;
  backoff_multiplier: number;
  max_backoff_seconds: number;
}

export interface ActorDescriptor {
  id: string;
  tenant: string;
  name: string;
  actor_type: ActorType;
  version: string;
  description?: string;
  capabilities: string[];
  required_permissions: string[];
  configuration: Record<string, any>;
  timeout_seconds?: number;
  retry_policy?: RetryPolicy;
  created_at: string;
  updated_at: string;
  created_by: string;
  active: boolean;
}

export interface CreateActorDescriptorInput {
  name: string;
  actor_type: ActorType;
  version: string;
  description?: string;
  capabilities: string[];
  required_permissions: string[];
  configuration: Record<string, any>;
  timeout_seconds?: number;
  retry_policy?: RetryPolicy;
}

export interface UpdateActorDescriptorInput {
  id: string;
  description?: string;
  capabilities?: string[];
  configuration?: Record<string, any>;
  timeout_seconds?: number;
  retry_policy?: RetryPolicy;
  active?: boolean;
}

export interface ActorDescriptorFilter {
  actor_type?: ActorType;
  active?: boolean;
  name_pattern?: string;
  capability?: string;
}

// ============================================================================
// GraphQL Operations
// ============================================================================

const ACTOR_DESCRIPTOR_FRAGMENT = gql`
  fragment ActorDescriptorSummaryFields on ActorDescriptor {
    id
    tenant
    name
    actorType
    version
    description
    capabilities
    requiredPermissions
    configuration
    timeoutSeconds
    retryPolicy {
      maxAttempts
      backoffSeconds
      backoffMultiplier
      maxBackoffSeconds
    }
    createdAt
    updatedAt
    createdBy
    active
  }
`;

const LIST_ACTOR_DESCRIPTORS = gql`
  query ListActorDescriptors($tenantId: String!, $filter: ActorDescriptorFilter) {
    listActorDescriptors(tenantId: $tenantId, filter: $filter) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

const GET_ACTOR_DESCRIPTOR = gql`
  query GetActorDescriptor($tenantId: String!, $id: String!) {
    getActorDescriptor(tenantId: $tenantId, id: $id) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

const GET_ACTOR_BY_NAME = gql`
  query GetActorByName($tenantId: String!, $name: String!) {
    getActorByName(tenantId: $tenantId, name: $name) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

const CREATE_ACTOR_DESCRIPTOR = gql`
  mutation CreateActorDescriptor($input: CreateActorDescriptorInput!) {
    createActorDescriptor(input: $input) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

const UPDATE_ACTOR_DESCRIPTOR = gql`
  mutation UpdateActorDescriptor($input: UpdateActorDescriptorInput!) {
    updateActorDescriptor(input: $input) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

const DELETE_ACTOR_DESCRIPTOR = gql`
  mutation DeleteActorDescriptor($tenantId: String!, $id: String!) {
    deleteActorDescriptor(tenantId: $tenantId, id: $id)
  }
`;

const LIST_ACTOR_VERSIONS = gql`
  query ListActorVersions($tenantId: String!, $name: String!) {
    listActorVersions(tenantId: $tenantId, name: $name) {
      ...ActorDescriptorSummaryFields
    }
  }
  ${ACTOR_DESCRIPTOR_FRAGMENT}
`;

// ============================================================================
// Hook Implementation
// ============================================================================

export function useActorDescriptor() {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [actors, setActors] = useState<ActorDescriptor[]>([]);
  const [selectedActor, setSelectedActor] = useState<ActorDescriptor | null>(null);
  const [loading, setLoading] = useState(false);

  // ============================================================================
  // List Actor Descriptors
  // ============================================================================

  const listActors = useCallback(
    async (filter?: ActorDescriptorFilter): Promise<ActorDescriptor[]> => {
      setLoading(true);
      try {
        const { data } = await client.query<Record<string, any>>({
          query: LIST_ACTOR_DESCRIPTORS,
          variables: { tenantId, filter },
        });

        if (!data?.listActorDescriptors) {
          return [];
        }

        const actorList = data?.listActorDescriptors.map(mapActorDescriptor);
        setActors(actorList);
        return actorList;
      } catch (error) {
        console.error('Failed to list actors:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load actors',
          color: 'red',
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  // ============================================================================
  // Get Actor Descriptor by ID
  // ============================================================================

  const getActor = useCallback(
    async (id: string): Promise<ActorDescriptor | null> => {
      setLoading(true);
      try {
        const { data } = await client.query<Record<string, any>>({
          query: GET_ACTOR_DESCRIPTOR,
          variables: { tenantId, id },
        });

        if (!data?.getActorDescriptor) {
          return null;
        }

        const actor = mapActorDescriptor(data?.getActorDescriptor);
        setSelectedActor(actor);
        return actor;
      } catch (error) {
        console.error('Failed to get actor:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load actor',
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  // ============================================================================
  // Get Actor by Name
  // ============================================================================

  const getActorByName = useCallback(
    async (name: string): Promise<ActorDescriptor | null> => {
      setLoading(true);
      try {
        const { data } = await client.query<Record<string, any>>({
          query: GET_ACTOR_BY_NAME,
          variables: { tenantId, name },
        });

        if (!data?.getActorByName) {
          return null;
        }

        const actor = mapActorDescriptor(data?.getActorByName);
        return actor;
      } catch (error) {
        console.error('Failed to get actor by name:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load actor',
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  // ============================================================================
  // Create Actor Descriptor
  // ============================================================================

  const createActor = useCallback(
    async (input: CreateActorDescriptorInput): Promise<ActorDescriptor | null> => {
      setLoading(true);
      try {
        const { data } = await client.mutate<Record<string, any>>({
          mutation: CREATE_ACTOR_DESCRIPTOR,
          variables: {
            input: {
              tenantId,
              ...input,
              // Map to GraphQL naming convention
              actorType: input.actor_type,
              requiredPermissions: input.required_permissions,
              timeoutSeconds: input.timeout_seconds,
              retryPolicy: input.retry_policy
                ? {
                    maxAttempts: input.retry_policy.max_attempts,
                    backoffSeconds: input.retry_policy.backoff_seconds,
                    backoffMultiplier: input.retry_policy.backoff_multiplier,
                    maxBackoffSeconds: input.retry_policy.max_backoff_seconds,
                  }
                : undefined,
            },
          },
        });

        if (!data?.createActorDescriptor) {
          throw new Error('Failed to create actor descriptor');
        }

        const actor = mapActorDescriptor(data?.createActorDescriptor);
        notifications.show({
          title: 'Success',
          message: `Actor "${input.name}" created successfully`,
          color: 'green',
        });

        // Refresh list
        await listActors();
        return actor;
      } catch (error) {
        console.error('Failed to create actor:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to create actor',
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listActors]
  );

  // ============================================================================
  // Update Actor Descriptor
  // ============================================================================

  const updateActor = useCallback(
    async (input: UpdateActorDescriptorInput): Promise<ActorDescriptor | null> => {
      setLoading(true);
      try {
        const { data } = await client.mutate<Record<string, any>>({
          mutation: UPDATE_ACTOR_DESCRIPTOR,
          variables: {
            input: {
              tenantId,
              ...input,
              // Map to GraphQL naming convention
              timeoutSeconds: input.timeout_seconds,
              retryPolicy: input.retry_policy
                ? {
                    maxAttempts: input.retry_policy.max_attempts,
                    backoffSeconds: input.retry_policy.backoff_seconds,
                    backoffMultiplier: input.retry_policy.backoff_multiplier,
                    maxBackoffSeconds: input.retry_policy.max_backoff_seconds,
                  }
                : undefined,
            },
          },
        });

        if (!data?.updateActorDescriptor) {
          throw new Error('Failed to update actor descriptor');
        }

        const actor = mapActorDescriptor(data?.updateActorDescriptor);
        notifications.show({
          title: 'Success',
          message: 'Actor updated successfully',
          color: 'green',
        });

        // Refresh list
        await listActors();
        return actor;
      } catch (error) {
        console.error('Failed to update actor:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to update actor',
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listActors]
  );

  // ============================================================================
  // Delete Actor Descriptor
  // ============================================================================

  const deleteActor = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      try {
        const { data } = await client.mutate<Record<string, any>>({
          mutation: DELETE_ACTOR_DESCRIPTOR,
          variables: { tenantId, id },
        });

        if (!data?.deleteActorDescriptor) {
          throw new Error('Failed to delete actor descriptor');
        }

        notifications.show({
          title: 'Success',
          message: 'Actor deleted successfully',
          color: 'green',
        });

        // Refresh list
        await listActors();
        return true;
      } catch (error) {
        console.error('Failed to delete actor:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete actor',
          color: 'red',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listActors]
  );

  // ============================================================================
  // List Actor Versions
  // ============================================================================

  const listActorVersions = useCallback(
    async (name: string): Promise<ActorDescriptor[]> => {
      setLoading(true);
      try {
        const { data } = await client.query<Record<string, any>>({
          query: LIST_ACTOR_VERSIONS,
          variables: { tenantId, name },
        });

        if (!data?.listActorVersions) {
          return [];
        }

        return data?.listActorVersions.map(mapActorDescriptor);
      } catch (error) {
        console.error('Failed to list actor versions:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load actor versions',
          color: 'red',
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  // ============================================================================
  // Auto-load actors on mount
  // ============================================================================

  useEffect(() => {
    listActors();
  }, [listActors]);

  // ============================================================================
  // Return Hook Interface
  // ============================================================================

  return {
    // State
    actors,
    selectedActor,
    loading,

    // Actions
    listActors,
    getActor,
    getActorByName,
    createActor,
    updateActor,
    deleteActor,
    listActorVersions,
    setSelectedActor,
  };
}

// ============================================================================
// Mapping Functions (GraphQL → Frontend Types)
// ============================================================================

function mapActorDescriptor(gqlData: any): ActorDescriptor {
  return {
    id: gqlData.id,
    tenant: gqlData.tenant,
    name: gqlData.name,
    actor_type: gqlData.actorType,
    version: gqlData.version,
    description: gqlData.description,
    capabilities: gqlData.capabilities,
    required_permissions: gqlData.requiredPermissions,
    configuration: gqlData.configuration,
    timeout_seconds: gqlData.timeoutSeconds,
    retry_policy: gqlData.retryPolicy
      ? {
          max_attempts: gqlData.retryPolicy.maxAttempts,
          backoff_seconds: gqlData.retryPolicy.backoffSeconds,
          backoff_multiplier: gqlData.retryPolicy.backoffMultiplier,
          max_backoff_seconds: gqlData.retryPolicy.maxBackoffSeconds,
        }
      : undefined,
    created_at: gqlData.createdAt,
    updated_at: gqlData.updatedAt,
    created_by: gqlData.createdBy,
    active: gqlData.active,
  };
}
